// DTOs + enum constants for the Onboarding page (7 tabs). Field names are the
// camelCased ABP JSON shapes. Enum value->label arrays mirror the backend
// [Display(Name)] order (numeric value = declaration order).

import type { PagedResult } from './abp';

export type { PagedResult };

export interface LookupDto {
  id: number;
  name: string;
}
export interface LookupGuidDto {
  id: string;
  name: string;
}
export interface EnumOption {
  value: number;
  name: string;
}

// Paginated input shared by every server-side table (ABP PaginatedInputDto).
export interface PaginatedInput {
  skipCount?: number;
  maxResultCount?: number;
  sorting?: string;
  search?: string;
  draw?: number;
}

// ---------------------------------------------------------------------------
// TAB 1 — SYMBOLS
// ---------------------------------------------------------------------------
export interface SymbolDto {
  id: string;
  name: string;
  symbolId: string; // real PK for update/delete
  symbolType: string;
  sbUnderlyingCurrency: string | null;
  assetClass: string;
  base: string;
  quote: string;
  quoteAssetClass: string;
  country: string | null;
  contractSize: number;
  dataSource: string;
  digits: number;
}

export interface CreateSymbolDto {
  name: string;
  symbolType: number;
  sbUnderlyingCurrency?: string | null;
  assetClass: number;
  base: string;
  quote: string;
  quoteAssetClass: number;
  country?: number | null;
  contractSize: number;
  digits: number;
  dataSource: number;
}

export type UpdateSymbolDto = Omit<CreateSymbolDto, 'contractSize'>;

export interface CreateSymbolsDatatableDto extends PaginatedInput {
  name?: string;
  symbolType?: string;
  sbUnderlyingCurrency?: string;
  assetClass?: string;
  base?: string;
  quote?: string;
  quoteAssetClass?: string;
  country?: string;
  contractSize?: string;
  dataSource?: string;
  digits?: string;
}

export interface MissingSymbolDto {
  name: string;
  symbolTypeId: number;
  symbolTypeName: string;
  dataSourceId: number;
  dataSourceName: string;
  assetClassName: string;
  assetClassId: number;
  base: string;
  quote: string;
  quoteAssetClassId: number;
  quoteAssetClassName: string;
  contractSize?: number;
  digits?: number;
  countryId?: number;
  countryName: string;
}

export interface SymbolDropdownDataDto {
  symbolTypes: LookupDto[];
  assetClasses: LookupDto[];
  countries: LookupDto[];
  quoteAssetClasses: LookupDto[];
  dataSources: LookupDto[];
}

export interface AddMissingSymbolsResultDto {
  addedCount: number;
  errors: string[];
}

export interface SymbolsUploadResultDto {
  created: number;
  updated: number;
  errors: string[];
}

export interface SymbolsUploadResponse {
  success: boolean;
  message?: string;
  data: SymbolsUploadResultDto;
}

// ---------------------------------------------------------------------------
// TAB 2 — TDI INSTRUMENTS
// ---------------------------------------------------------------------------
export interface DebtInstrumentDto {
  id: string;
  issue: string;
  currency: string;
  instrumentType: string;
  modifiedDuration: number;
  residualMaturity: string;
  issuerType: string;
  creditQualityStep: number;
  lossGivenDefault: number;
  contractSize: number;
  dataSource: string;
  digits: number;
}

export interface CreateDebtInstrumentDto {
  name: string;
  base: string;
  quote: string;
  symbolTypeId: number;
  dataSourceId: number;
  contractSize: number;
  digits: number;
  modifiedDuration: number;
  residualMaturity: number;
  issuerType: number;
  creditQualityStep: number;
  lossGivenDefault: number;
  faceValue: number;
  mifir: boolean;
}

export interface UpdateDebtInstrumentDto {
  id: string;
  base: string;
  quote: string;
  symbolTypeId: number;
  dataSourceId: number;
  contractSize: number;
  digits: number;
  modifiedDuration: number;
  residualMaturity: number;
  issuerType: number;
  creditQualityStep: number;
  lossGivenDefault: number;
  faceValue: number;
  mifir: boolean;
}

export interface DebtInstrumentDropdownDataDto {
  currencies: LookupDto[];
  symbolTypes: LookupDto[];
  dataSources: LookupDto[];
  residualMaturityOptions: LookupDto[];
  issuerTypeOptions: LookupDto[];
  creditQualitySteps: number[];
}

export interface DebtSymbolFiltersDto extends PaginatedInput {
  issue?: string;
  currency?: string;
  instrumentType?: string;
  modifiedDuration?: string;
  residualMaturity?: string;
  issuerType?: string;
  creditQualityStep?: string;
  contractSize?: string;
  dataSource?: string;
  lossGivenDefault?: string;
  digits?: string;
}

export const FACE_VALUES = [100, 1000] as const;
export const CREDIT_QUALITY_STEPS = [1, 2, 3, 4, 5, 6] as const;

// ---------------------------------------------------------------------------
// TAB 3 — ASSET CLASSES
// ---------------------------------------------------------------------------
export interface AssetClassDto {
  id: number;
  label: string | null;
  description: string | null;
  ccg: number | null;
  ccn: number | null;
  pfe: number | null;
  type: string | null;
}

export interface CreateAssetClassDto {
  label: string;
  description: string;
  ccg: number | null;
  ccn: number | null;
  pfe: number | null;
  parentId: number | null;
}

export interface AssetClassesFiltersDatatableDto extends PaginatedInput {
  label?: string;
  description?: string;
  ccg?: string;
  ccn?: string;
  pfe?: string;
  type?: string;
}

// ---------------------------------------------------------------------------
// TAB 4 — EXECUTION VENUES
// ---------------------------------------------------------------------------
export interface ExecutionVenueDto {
  id: string;
  nameOfEntity: string;
  leiCode: string;
  countryId: number;
  country: string;
  typeOfEntity: string;
  relation: string;
}

export interface CreateExecutionVenueDto {
  leiCode: string;
  nameOfEntity: string;
  countryId: number;
  typeOfEntity: number;
  relation: number;
}

export interface UpdateExecutionVenueDto {
  countryId: number;
  typeOfEntity: number;
  relation: number;
}

export interface CreateExecutionVenueResultDto {
  status: 'Created' | 'PendingConfirmation';
  message: string;
  venue: ExecutionVenueDto | null;
  pendingData: CreateExecutionVenueDto | null;
}

export interface ExecutionVenueLookupDto {
  countries: LookupDto[];
  typesOfEntity: EnumOption[];
  relations: EnumOption[];
}

export interface ExecutionVenueDatatableDto extends PaginatedInput {
  nameOfEntity?: string;
  leiCode?: string;
  country?: string;
  typeOfEntity?: string;
  relation?: string;
}

// ---------------------------------------------------------------------------
// TAB 5 — GROUP CATEGORIES
// ---------------------------------------------------------------------------
export interface GroupCategoryDto {
  id: string;
  groupId: string;
  name: string;
  currency: string;
  currencyId: number;
  miFIDClassification: string;
  clientType: string;
  executionVenue: string;
  leiCode: string | null;
  assetType: string;
  counterpartyTypeCVA: string;
  dataSource: string;
  dataSourceId: number;
}

export interface CreateGroupCategoryDto {
  name: string;
  currencyId: number;
  miFIDClassification: number;
  clientType: number;
  executionVenue: number;
  leiCode: string | null;
  assetType: number;
  counterpartyTypeCVA: number;
  dataSourceId: number;
}

export interface UpdateGroupCategoryDto {
  miFIDClassification: number;
  clientType: number;
  executionVenue: number;
  leiCode: string | null;
  assetType: number;
  counterpartyTypeCVA: number;
}

export interface GroupCategoryFilterDto extends PaginatedInput {
  name?: string;
  currency?: string;
  miFIDClassification?: string;
  clientType?: string;
  executionVenue?: string;
  leiCode?: string;
  assetType?: string;
  counterpartyTypeCVA?: string;
  dataSource?: string;
}

export interface GroupCategoryDropdownDataDto {
  currencies: LookupDto[];
  dataSources: LookupDto[];
  leiCodes: LookupGuidDto[]; // JS uses name as the option value
  miFIDClassifications: EnumOption[];
  clientTypes: EnumOption[];
  executionVenues: EnumOption[];
  assetTypes: EnumOption[];
  counterpartyTypes: EnumOption[];
  clientLeiCode: string | null;
}

// Market Maker = 0 drives the LEI auto-fill/disable behaviour.
export const GROUP_EXECUTION_VENUE_MARKET_MAKER = 0;

// ---------------------------------------------------------------------------
// TAB 6 — LOGIN INFORMATION (read-only)
// ---------------------------------------------------------------------------
export interface LoginInfoDto {
  id: string;
  login: number;
  uniqueId: string;
  group: string;
  country: string;
  amlRisk: string | null;
  netWorth: number | null;
  annualIncome: number | null;
  incomeSource: string | null;
  mifidClassification: string;
  dataSource: string;
  dataSourceId: number;
  birthdate: string | null;
  riskOfLoss: number | null;
  unstable: string;
}

export interface LoginInfoDatatableFiltersDto extends PaginatedInput {
  login?: string;
  uniqueId?: string;
  group?: string;
  country?: string;
  amlRisk?: string;
  netWorth?: string;
  annualIncome?: string;
  incomeSource?: string;
  mifidClassification?: string;
  dataSource?: string;
  birthdate?: string;
  riskOfLoss?: string;
}

// ---------------------------------------------------------------------------
// TAB 7 — PORTFOLIOS
// ---------------------------------------------------------------------------
export interface PortfolioDto {
  id: number;
  name: string;
  groups: string;
  groupTextFilter: string;
  lPs: string;
  logins: string;
}

export interface CreatePortfolioDto {
  name: string;
  groupTextFilter: string | null;
  groupsIds: string[];
  loginsIds: Record<string, number>;
  lPsIds: number[];
}

export type UpdatePortfolioDto = CreatePortfolioDto;

export interface PortfolioDatatableDto extends PaginatedInput {
  name?: string;
  groups?: string;
  groupTextFilter?: string;
  lPs?: string;
  logins?: string;
}

export interface LoginDropdownDto {
  id: string;
  name: string;
  dataSourceId: number;
}

export interface PortfoliosDropdownDataDto {
  logins: LoginDropdownDto[];
  lPs: LookupDto[];
  groups: LookupGuidDto[];
}
